import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Check, Download, Loader2 } from 'lucide-react';
import { uploadMedia } from '@littlebooker/dal';
import { Button } from '@/components/ui/button';
import { CardEditor, type CardEditorHandle, type CardValue } from './CardEditor';
import { exportExt, type ExportFormat } from './photoSlot';
import { showCardTemplates, type CardTemplate, type Variant } from './templates';

// Generated cards land in their own storage folder (see DAL storageCategoryFolder).
const CARD_MEDIA_CATEGORY = 'social_card' as const;

/** Cards are opaque photos, so JPEG keeps them small without visible loss. */
const CARD_FORMAT: ExportFormat = 'jpeg';

const slugify = (s: string) =>
  (s || 'artist').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export interface ShowCardsPanelResult {
  /** True when cards were rendered, uploaded and persisted. */
  uploaded: boolean;
  /** Set when the attempt failed. The panel shows it too, if it is still mounted. */
  error?: string;
}

export interface ShowCardsPanelHandle {
  /**
   * Render and upload the cards, then hand the new map to `onSave` — but only when
   * there is something new to write (a fresh photo, a crop that was moved, or a
   * card that was never saved).
   *
   * Never rejects: cards are a by-product of saving the show, so a failure is
   * reported in the result rather than failing the save that triggered it.
   */
  saveIfNeeded: () => Promise<ShowCardsPanelResult>;
}

export interface ShowCardsPanelProps {
  /** The show photo the cards are built from. Cards need a photo to render. */
  photoSrc: string | null;
  artistName: string;
  /** Show/programme name — only drawn by the resident layouts. */
  showName?: string;
  variant?: Variant;
  /** Card URLs already saved on the show, keyed by template id. */
  savedCards?: Record<string, string>;
  accountSlug?: string;
  /** Anonymous-upload auth for the artist-facing flow (no admin JWT). */
  uploadAuth?: { token: string; entityId: string };
  /**
   * Persist the new card map on the show. Receives the full map (existing entries
   * merged with the ones just uploaded), so a caller can patch it straight onto
   * `metadata.social_cards`.
   */
  onSave: (cards: Record<string, string>) => Promise<void>;
  /**
   * Cap each preview's on-screen height, in CSS px. The editor otherwise grows to
   * 72% of the viewport height, which is right on a page of its own and far too
   * tall inside a modal. Capping the box's width to the matching aspect ratio is
   * what limits the height, since the editor fits itself to its container.
   *
   * Export is unaffected: the stage is always rendered at 2x the template's
   * logical canvas, whatever it is displayed at.
   */
  previewMaxHeight?: number;
  className?: string;
}

/**
 * The set of announcement cards for one show, all built from the show photo: one
 * per platform, each croppable on its own (the frames differ in shape, so a crop
 * that works for the 4:5 Instagram post rarely works for the square SoundCloud
 * one).
 *
 * There is no save button of its own — the cards ride along with the show form's
 * save, which the parent triggers through `saveIfNeeded` on the ref. Re-saving
 * replaces what is there: the new URLs overwrite the old entries, and the
 * superseded objects are swept server-side (see the public social-cards endpoint
 * and deleteHappening).
 */
export const ShowCardsPanel = forwardRef<ShowCardsPanelHandle, ShowCardsPanelProps>(
  function ShowCardsPanel(
    {
      photoSrc,
      artistName,
      showName,
      variant = 'guest',
      savedCards = {},
      accountSlug,
      uploadAuth,
      onSave,
      previewMaxHeight,
      className,
    },
    ref,
  ) {
    const templates = useMemo(() => showCardTemplates(variant), [variant]);

    const [values, setValues] = useState<Record<string, CardValue>>({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [justSaved, setJustSaved] = useState(false);

    const editorRefs = useRef<Record<string, CardEditorHandle | null>>({});
    // The photo the cards on the show were last built from, and whether the artist
    // has moved a crop since. Together they answer "is there anything to upload?",
    // so an unrelated form save (a tracklist edit, say) doesn't re-render and
    // re-upload two images every time.
    const savedPhotoRef = useRef<string | null>(photoSrc);
    const touchedRef = useRef(false);

    // A new photo (or a different layout) means every crop starts over — each frame
    // cover-fits the fresh image.
    useEffect(() => {
      setValues(
        Object.fromEntries(
          templates.map((t) => [t.id, { artistName: '', photoSrc, transform: null } as CardValue]),
        ),
      );
      setJustSaved(false);
      setError(null);
      // The names are folded in by the effect below, which must not reset crops.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [photoSrc, templates]);

    // Editing the show title re-draws the label; it must not throw away the crops.
    useEffect(() => {
      setValues((prev) =>
        Object.fromEntries(
          Object.entries(prev).map(([id, v]) => [id, { ...v, artistName, showName }]),
        ),
      );
    }, [artistName, showName]);

    // Read alongside the state so setValue can compare against the current crop
    // without doing the comparison inside a state updater.
    const valuesRef = useRef(values);
    valuesRef.current = values;

    const setValue = useCallback((templateId: string, next: CardValue) => {
      const prev = valuesRef.current[templateId];
      // The editor's first onChange is its own cover-fit of a freshly loaded photo
      // (transform was null), not the artist moving anything.
      if (prev?.transform && next.transform !== prev.transform) {
        touchedRef.current = true;
        setJustSaved(false);
      }
      setValues((current) => ({ ...current, [templateId]: next }));
    }, []);

    const download = (template: CardTemplate) => {
      const url = editorRefs.current[template.id]?.toDataURL(CARD_FORMAT);
      if (!url) return;
      const a = document.createElement('a');
      a.href = url;
      a.download = `${slugify(artistName)}-${template.id}.${exportExt(CARD_FORMAT)}`;
      a.click();
    };

    const needsUpload = () => {
      if (!photoSrc) return false;
      if (touchedRef.current) return true;
      if (savedPhotoRef.current !== photoSrc) return true;
      return templates.some((t) => !savedCards[t.id]);
    };

    useImperativeHandle(ref, () => ({
      saveIfNeeded: async () => {
        if (!needsUpload()) return { uploaded: false };
        setSaving(true);
        setError(null);
        try {
          const uploaded: Record<string, string> = {};
          for (const template of templates) {
            const blob = await editorRefs.current[template.id]?.toBlob(CARD_FORMAT);
            if (!blob) {
              throw new Error(`Could not render the ${template.platformLabel} card.`);
            }
            const base = `${slugify(artistName)}-${template.id}`;
            const result = await uploadMedia(blob, {
              category: CARD_MEDIA_CATEGORY,
              identifier: base,
              fileName: `${base}.${exportExt(CARD_FORMAT)}`,
              accountSlug,
              auth: uploadAuth,
            });
            uploaded[template.id] = result.url;
          }
          // One write with every card, so a half-finished batch never lands.
          await onSave({ ...savedCards, ...uploaded });
          savedPhotoRef.current = photoSrc;
          touchedRef.current = false;
          setJustSaved(true);
          return { uploaded: true };
        } catch (err) {
          const message =
            err instanceof Error
              ? `The show was saved, but the cards were not: ${err.message}`
              : 'The show was saved, but the cards were not.';
          setError(message);
          return { uploaded: false, error: message };
        } finally {
          setSaving(false);
        }
      },
    }));

    if (!photoSrc) {
      return (
        <div className={className}>
          <p className="rounded-md border border-dashed border-input px-4 py-6 text-center text-sm text-muted-foreground">
            Upload a show photo above and your announcement cards appear here.
          </p>
        </div>
      );
    }

    return (
      <div className={className}>
        <div className="grid gap-6 sm:grid-cols-2">
          {templates.map((template) => (
            <div key={template.id} className="space-y-2">
              <span className="text-sm font-medium">{template.platformLabel}</span>
              <div
                className="mx-auto w-full"
                style={
                  previewMaxHeight
                    ? {
                        maxWidth: Math.round(
                          (previewMaxHeight * template.canvas.width) / template.canvas.height,
                        ),
                      }
                    : undefined
                }
              >
                <CardEditor
                  ref={(handle) => {
                    editorRefs.current[template.id] = handle;
                  }}
                  template={template}
                  value={values[template.id] ?? { artistName, showName, photoSrc, transform: null }}
                  onChange={(next) => setValue(template.id, next)}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => download(template)}
              >
                <Download className="mr-2 h-4 w-4" />
                Download
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-3 text-xs text-muted-foreground">
          {saving ? (
            <span className="flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Saving your cards…
            </span>
          ) : justSaved ? (
            <span className="flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5" />
              Cards saved to the show.
            </span>
          ) : (
            'Drag a photo to reposition it and use the zoom slider under each card. Your cards save with the rest of the form.'
          )}
        </div>
        {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
      </div>
    );
  },
);
